<!-- eslint-disable vue/html-self-closing -->
<template>
  <section class="gf-custom-search">
    <div class="gf-custom-search__content">
      <div
        ref="optionsCreator"
        v-click-outside="onSearchUnfocus"
        class="gf-custom-search__creator"
        @click="onSearchClick"
      >
        <gf-custom-search-item
          v-for="(state, index) in optionsState"
          :key="state.modelIndex"
          :ref="`searchItem${index}`"
          :is-active="state.isActive"
          :available-option-types="availableOptionTypes.filter((option) => option.canBeUsed)"
          :option-index="state.modelIndex"
          :selected-item="selectedFiltersModel[index]"
          @submit="onSearchItemSubmit"
          @delete="onOptionItemDelete"
          @search="submitSearchParams"
          @focusOn="onFocusOn"
          @focusOnPrevious="onFocusOnPrevious"
          @unfocus="onSearchUnfocus"
        />
        <input
          v-if="activeOptionIndex === null"
          ref="optionsCreatorInput"
          class="gf-custom-search__input"
          type="text"
          :placeholder="placeholder"
        />
      </div>
    </div>
    <div class="gf-custom-search__actions">
      <button
        v-if="optionsModel?.length > 0"
        type="button"
        class="btn btn-sm text-muted"
        @click="cleanSearchParams"
      >
        <i class="gf-icon gf-icon-x"></i>
      </button>
      <button
        type="button"
        class="btn btn-sm"
        @click="submitSearchParams"
      >
        <i class="gf-icon gf-icon-search"></i>
      </button>
    </div>
  </section>
</template>

<script>
import Vue from 'vue';
// eslint-disable-next-line max-len
import GfCustomSearchItem from '@/shared-modules/shared-components/shared/filter/GfCustomSearchItem';
import { clickOutsideDirective } from '@/directives';

export default {
  name: 'GfCustomSearch',
  components: {
    GfCustomSearchItem
  },
  directives: {
    'click-outside': clickOutsideDirective()
  },
  props: {
    optionTypes: {
      type: Array,
      default() {
        return [];
      }
    },
    selectedFilters: {
      type: Array,
      default() {
        return [];
      }
    },
    maxOptionsLength: {
      type: Number,
      default: 1
    },
    placeholder: {
      type: String,
      default() {
        return this.$t('message.searchPlaceholder');
      }
    }
  },
  data() {
    return {
      optionsState: [],
      optionsModel: [],
      activeOptionIndex: null,
      availableOptionTypes: [],
      selectedFiltersModel: []
    };
  },
  computed: {
    canFocus() {
      return this.activeOptionIndex !== null && this.activeOptionIndex !== -1;
    },
    activeOptionElement() {
      return this.$refs[`searchItem${this.activeOptionIndex}`][0];
    },
    activeOptionElementInfo() {
      return this.$refs[`searchItem${this.activeOptionIndex}`][0]?.getOptionInfo();
    },
    optionsAlreadySubmited() {
      let isAlreadySubmited = this.optionsModel?.length === 0;
      let queryFilters = {};

      for (let queryKey of Object.keys(this.$route.query)) {
        let queryParamValue = this.$route.query[queryKey];
        const optionIndex = this.optionTypes.findIndex((element) => element.key === queryKey);
        if (optionIndex !== -1) {
          queryFilters[queryKey] = queryParamValue;
        }
      }

      if (Object.keys(queryFilters)?.length !== this.optionsModel?.length) {
        return false;
      } else {
        for (let option of this.optionsModel) {
          if (queryFilters[option.type.key] === option.value) {
            isAlreadySubmited = true;
          } else {
            isAlreadySubmited = false;
            break;
          }
        }
      }

      return isAlreadySubmited;
    }
  },
  watch: {
    selectedFilters() {
      this.selectedFiltersModel = this.selectedFilters;
      this.fillOptionsBySelectedFilters();
    }
  },
  mounted() {
    this.selectedFiltersModel = this.selectedFilters;
    this.fillOptionsState();
    this.prepareAvailableOptionTypes();
    this.activeOptionIndex = null;
    this.onSearchUnfocus();
    document.addEventListener('keydown', this.onKeyDown);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeyDown);
  },
  methods: {
    onKeyDown(e) {
      if (e.keyCode === 13 && (e.metaKey || e.ctrlKey)) {
        this.submitSearchParams();
      } else if (e.keyCode === 13) {
        if (this.optionsModel?.length < this.maxOptionsLength) {
          const currentOptionElement = this.$refs[`searchItem${this.activeOptionIndex}`][0];
          const currentOptionInfo = currentOptionElement?.getOptionInfo();

          if (
            !currentOptionInfo?.option?.value &&
            !currentOptionElement.getFocusedOptionTypeKey()?.key
          ) {
            this.submitSearchParams();
          } else if (currentOptionInfo?.option?.value && currentOptionInfo?.option?.type) {
            currentOptionElement?.submitOption();
          }
        } else {
          this.submitSearchParams();
        }
      }
    },
    onSearchClick(e) {
      if (e.target === this.$refs.optionsCreator || e.target === this.$refs.optionsCreatorInput) {
        this.onSearchFocus();
      }
    },
    onSearchFocus() {
      if (!this.activeOptionIndex) {
        this.activeOptionIndex = 0;
      }
      if (this.canFocus) {
        this.changeActiveOptionFocus(true);
      }
    },
    onSearchUnfocus() {
      if (this.canFocus) {
        if (this.activeOptionElementInfo.option.type && this.activeOptionElementInfo.option.value) {
          this.onSearchItemSubmit(this.activeOptionElementInfo);
        } else if (this.activeOptionElementInfo.index < this.optionsModel.length) {
          this.activeOptionElement.deleteOption();
        } else {
          this.activeOptionElement.resetOptionModel();
        }
        this.changeActiveOptionFocus(false);
      }
      if (this.optionsModel?.length === 0) {
        this.activeOptionIndex = null;
      } else if (this.optionsModel?.length === this.maxOptionsLength) {
        this.activeOptionIndex = -1;
      } else {
        this.activeOptionIndex = this.optionsModel.length;
      }
    },

    onSearchItemSubmit(data) {
      if (this.canFocus) {
        this.changeActiveOptionFocus(false);
      }
      this.setActiveOptionByIndex(data.index);
      this.addNewOptionItem(data);
      this.processUsedOptionType(data);
      this.moveToNewOption();
    },
    onOptionItemDelete(data) {
      if (this.canFocus) {
        this.changeActiveOptionFocus(false);
      }
      this.setActiveOptionByIndex(data.index);
      this.deleteOptionItem();
      this.returnUsedOptionType(data);
      this.moveToOldOption(data);
    },
    onFocusOn(data) {
      if (this.canFocus) {
        this.changeActiveOptionFocus(false);
      }
      this.setActiveOptionByIndex(data.index);
      this.changeActiveOptionFocus(true);
    },
    onFocusOnPrevious() {
      this.changeActiveOptionFocus(false);
      this.activeOptionIndex -= 1;
      if (this.canFocus) {
        this.changeActiveOptionFocus(true);
      } else {
        this.onSearchUnfocus();
      }
    },
    setActiveOptionByIndex(index) {
      this.activeOptionIndex = this.optionsState.findIndex(
        (element) => element.modelIndex === index
      );
    },
    changeActiveOptionFocus(isActive) {
      Vue.set(this.optionsState, this.activeOptionIndex, {
        modelIndex: this.optionsState[this.activeOptionIndex].modelIndex,
        isActive
      });
    },
    processUsedOptionType(data) {
      if (!data.option.type?.isMultiple) {
        this.availableOptionTypes[data.option.type.index].canBeUsed = false;
      }
    },
    returnUsedOptionType(data) {
      if (!data.option.type?.isMultiple && data.option.type) {
        this.availableOptionTypes[data.option.type.index].canBeUsed = true;
      }
    },
    addNewOptionItem(data) {
      if (this.activeOptionIndex < this.optionsModel.length) {
        this.optionsModel[this.activeOptionIndex] = data.option;
      } else {
        this.optionsModel.push(data.option);
      }
    },
    deleteOptionItem() {
      this.optionsModel.splice(this.activeOptionIndex, 1);
      this.selectedFiltersModel.splice(this.activeOptionIndex, 1);
    },
    moveToNewOption() {
      if (this.optionsModel?.length === this.maxOptionsLength) {
        this.activeOptionIndex = -1;
      } else if (this.activeOptionIndex < this.optionsModel.length - 1) {
        this.activeOptionIndex = this.optionsModel.length - 1;
      } else if (this.activeOptionIndex < this.maxOptionsLength - 1) {
        Vue.set(this.optionsState, this.activeOptionIndex + 1, {
          modelIndex: this.optionsState[this.activeOptionIndex + 1].modelIndex,
          isActive: true
        });
        this.activeOptionIndex += 1;
      } else {
        this.activeOptionIndex = -1;
      }
    },
    moveToOldOption(data) {
      if (this.optionsModel?.length > 0) {
        const resultOprionsState = this.optionsState;
        for (let i = this.activeOptionIndex; i < this.maxOptionsLength - 1; i++) {
          resultOprionsState[i] = resultOprionsState[i + 1];
        }
        resultOprionsState[this.optionsModel.length] = {
          modelIndex: data.index,
          isActive: true
        };
        this.optionsState = resultOprionsState;
        this.activeOptionIndex = this.optionsModel?.length;
      } else {
        Vue.set(this.optionsState, 0, {
          modelIndex: this.optionsState[this.activeOptionIndex].modelIndex,
          isActive: true
        });
        this.activeOptionIndex = 0;
      }
    },
    fillOptionsState() {
      this.optionsState = [];
      for (let i = 0; i < this.maxOptionsLength; i++) {
        this.optionsState.push({ modelIndex: i, isActive: false });
      }
    },
    fillOptionsBySelectedFilters() {
      if (this.optionsModel.length === 0) {
        for (let i = 0; i < this.selectedFiltersModel.length; i++) {
          this.onSearchItemSubmit({
            index: i,
            option: this.selectedFiltersModel[i]
          });
        }
        if (this.optionsModel.length === this.maxOptionsLength) {
          this.activeOptionIndex = -1;
        } else {
          this.optionsState[this.activeOptionIndex].isActive = false;
          this.activeOptionIndex -= 1;
          this.optionsModel[this.activeOptionIndex].isActive = false;
          this.activeOptionIndex = this.optionsModel.length - 1;
          this.optionsModel[this.activeOptionIndex].isActive = true;
        }
      }
    },
    prepareAvailableOptionTypes() {
      this.availableOptionTypes = this.optionTypes;
      this.availableOptionTypes.forEach((option) => {
        option.canBeUsed = true;
      });
    },
    cleanSearchParams() {
      if (this.optionsModel.length > 0) {
        for (let i = this.optionsModel.length - 1; i >= 0; i--) {
          this.activeOptionIndex = i;
          this.activeOptionElement.deleteOption();
        }
        this.fillOptionsState();
        this.onSearchFocus();
        this.$emit('clean');
      }
    },
    submitSearchParams() {
      if (!this.optionsAlreadySubmited) {
        this.onSearchUnfocus();
        this.$emit('submit', this.optionsModel);
      }
    }
  }
};
</script>

<style lang="scss">
$this: '.gf-custom-search';

#{$this} {
  min-height: 42px;
  width: 100%;
  padding: 6px 8px;
  display: flex;
  flex-grow: 1;
  align-items: center;
  gap: 8px;
  background-color: var(--light);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);

  @include theme.md {
    min-height: 30px;
    padding: 4px 8px;
  }

  #{$this}__content {
    width: 100%;

    #{$this}__creator {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 12px;

      @include theme.lg {
        flex-wrap: nowrap;
      }

      #{$this}__input {
        width: 100%;
        padding: 0;
        background-color: transparent;
        border-style: none;
        outline: none;
        color: var(--text);
      }
    }
  }
  #{$this}__actions {
    display: flex;
    gap: 4px;

    & > button {
      min-height: auto;
      padding: 4px;

      @include theme.md {
        padding: 0;
      }

      &:focus {
        box-shadow: none;
      }
    }
  }
}
</style>
